import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box, Button, TextField, Typography, Select, MenuItem, FormControl,
    Divider, Stack
} from '@mui/material';
import * as api from './companiesApi';
import type {
    Company, CompanyType, Currency, User, Language, ShippingMethod,
    DeliveryTerm, PaymentTerm, PricingModel, PriceList, AddressType,
    ContactInfoType, Address, ContactInfo, ResellerPriority, CompanyRequest
} from './companiesApi';
import { useDetailView } from '../common/useDetailView';
import { sx14, FormRow, DetailViewShell } from '../common/detailView';

interface CompanyViewProps {
    companyId: number | null;
}

export function CompanyView({ companyId }: CompanyViewProps) {
    const { t } = useTranslation();
    const detail = useDetailView<Company>();

    const [companyTypes, setCompanyTypes] = useState<CompanyType[]>([]);
    const [currencies, setCurrencies] = useState<Currency[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [languages, setLanguages] = useState<Language[]>([]);
    const [shippingMethods, setShippingMethods] = useState<ShippingMethod[]>([]);
    const [deliveryTerms, setDeliveryTerms] = useState<DeliveryTerm[]>([]);
    const [paymentTerms, setPaymentTerms] = useState<PaymentTerm[]>([]);
    const [pricingModels, setPricingModels] = useState<PricingModel[]>([]);
    const [priceLists, setPriceLists] = useState<PriceList[]>([]);
    const [addressTypes, setAddressTypes] = useState<AddressType[]>([]);
    const [contactInfoTypes, setContactInfoTypes] = useState<ContactInfoType[]>([]);

    const [priceModelObject, setPriceModelObject] = useState<PricingModel | null>(null);

    const isNew = companyId === null;

    const resellerPriorities: ResellerPriority[] = [
        { code: 'A', displayName: 'A - ' + t('resellerorder.priorityA') },
        { code: 'B', displayName: 'B - ' + t('resellerorder.priorityB') },
        { code: 'C', displayName: 'C - ' + t('resellerorder.priorityC') },
        { code: 'D', displayName: 'D - ' + t('resellerorder.priorityD') },
        { code: 'S', displayName: 'S - ' + t('resellerorder.priorityS') }
    ];

    const loadData = useCallback(async () => {
        await detail.withLoading(async () => {
            const [typesData, currenciesData, usersData, languagesData, shippingData,
                deliveryData, paymentData, pricingData, priceListsData, addressTypesData,
                contactInfoTypesData] = await Promise.all([
                api.getCompanyTypes(),
                api.getCurrencies(),
                api.getActiveUsers(),
                api.getLanguages(),
                api.getShippingMethods(),
                api.getDeliveryTerms(),
                api.getPaymentTerms(),
                api.getPricingModels(),
                api.getPriceListBriefs(),
                api.getAddressTypes(),
                api.getContactInfoTypes(),
            ]);

            setCompanyTypes(typesData);
            setCurrencies(currenciesData);
            setUsers(usersData);
            setLanguages(languagesData);
            setShippingMethods(shippingData);
            setDeliveryTerms(deliveryData);
            setPaymentTerms(paymentData);
            setPricingModels(pricingData.priceModels);
            setPriceLists(priceListsData);
            setAddressTypes(addressTypesData);
            setContactInfoTypes(contactInfoTypesData);

            if (isNew) {
                const newCompany: Company = {
                    id: -1,
                    companyName: '',
                    companyType: typesData[0] ?? null,
                    language: languagesData[0] ?? null,
                    purchasingCurrency: currenciesData[0] ?? null,
                    deliveryTerm: null,
                    paymentTerm: null,
                    shippingMethod: null,
                    primaryPurchaser: null,
                    resellerPriceList: null,
                    addresses: [],
                    contactInfos: [],
                    commissionPercent: 0,
                };
                detail.setItem(newCompany);
                setPriceModelObject(pricingData.priceModels[0] ?? null);
                detail.setIsNew(true);
                detail.setIsEditing(true);
            } else {
                const companyData = await api.getCompany(companyId!);
                companyData.commissionPercent = companyData.commissionPercent
                    ? companyData.commissionPercent * 100
                    : 0;
                hydrateAddressTypes(companyData, addressTypesData);
                hydratePriority(companyData, resellerPriorities);
                detail.setItem(companyData);
                const pm = pricingData.priceModels.find(m => m.name === companyData.priceModel) ?? pricingData.priceModels[0];
                setPriceModelObject(pm ?? null);
                detail.setIsNew(false);
                detail.setIsEditing(false);
            }
        });
    }, [companyId, isNew]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const company = detail.item;

    // Validation
    const hasCompanyName = (): boolean => !!company?.companyName && company.companyName.trim().length > 0;
    const hasDeliveryTerm = (): boolean => !!company?.deliveryTerm && company.deliveryTerm.id > 0;
    const hasPaymentTerm = (): boolean => !!company?.paymentTerm && company.paymentTerm.id > 0;
    const hasShippingMethod = (): boolean => !!company?.shippingMethod && company.shippingMethod.id > 0;
    const hasResellerPriceList = (): boolean => {
        if (company?.companyType?.type === 'reseller') {
            return !!company.resellerPriceList && company.resellerPriceList.id > 0;
        }
        return false;
    };
    const allInformationIsValid = (): boolean =>
        hasCompanyName() && hasDeliveryTerm() && hasPaymentTerm() && hasShippingMethod();

    const availableAddressTypes = addressTypes.filter(at =>
        !(company?.addresses ?? []).some(a => a.addressType?.id === at.id)
    );

    const availableContactInfoTypes = contactInfoTypes.filter(ct =>
        !(company?.contactInfos ?? []).some(c => c.contactInfoType?.id === ct.id)
    );

    // Actions
    const startEdit = () => {
        detail.startEdit();
    };

    const cancelEdit = () => {
        detail.cancelEdit();
    };

    const save = async () => {
        if (!company) return;
        await detail.withSaving(async () => {
            const request = createCompanyRequest(company, priceModelObject);
            if (detail.isNew) {
                const response = await api.createCompany(request);
                response.commissionPercent = response.commissionPercent ? response.commissionPercent * 100 : 0;
                hydrateAddressTypes(response, addressTypes);
                hydratePriority(response, resellerPriorities);
                detail.setItem(response);
                detail.setIsNew(false);
                detail.setIsEditing(false);
            } else {
                const response = await api.updateCompany(company.id, request);
                response.commissionPercent = response.commissionPercent ? response.commissionPercent * 100 : 0;
                hydrateAddressTypes(response, addressTypes);
                hydratePriority(response, resellerPriorities);
                detail.setItem(response);
                detail.setIsEditing(false);
            }
        });
    };

    const addNewAddress = (addressType: AddressType) => {
        if (!company) return;
        const newAddresses = [...(company.addresses ?? []), { addressType } as Address];
        detail.update('addresses', newAddresses);
    };

    const removeAddress = (address: Address) => {
        if (!company) return;
        const newAddresses = (company.addresses ?? []).filter(a => a.addressType?.id !== address.addressType?.id);
        detail.update('addresses', newAddresses);
    };

    const updateAddressField = (address: Address, field: string, value: string) => {
        if (!company) return;
        const newAddresses = (company.addresses ?? []).map(a =>
            a.addressType?.id === address.addressType?.id ? { ...a, [field]: value } : a
        );
        detail.update('addresses', newAddresses);
    };

    const addNewContactInfo = (contactInfoType: ContactInfoType) => {
        if (!company) return;
        const newInfos = [...(company.contactInfos ?? []), { contactInfoType } as ContactInfo];
        detail.update('contactInfos', newInfos);
    };

    const removeContactInfo = (info: ContactInfo) => {
        if (!company) return;
        const newInfos = (company.contactInfos ?? []).filter(c => c.contactInfoType?.id !== info.contactInfoType?.id);
        detail.update('contactInfos', newInfos);
    };

    const updateContactInfoField = (info: ContactInfo, field: string, value: string) => {
        if (!company) return;
        const newInfos = (company.contactInfos ?? []).map(c =>
            c.contactInfoType?.id === info.contactInfoType?.id ? { ...c, [field]: value } : c
        );
        detail.update('contactInfos', newInfos);
    };

    return (
        <DetailViewShell<Company>
            title={company?.companyName ?? ''}
            isEditing={detail.isEditing}
            isNew={detail.isNew}
            saving={detail.saving}
            canSave={allInformationIsValid()}
            loading={detail.loading}
            error={detail.error}
            item={company}
            onEdit={startEdit}
            onSave={save}
            onCancel={cancelEdit}
            onClearError={() => detail.setError(null)}
        >
            {(item) => {
                const isReseller = item.companyType?.type === 'reseller';
                return (<>
                    {/* Company Name */}
                    <FormRow label={t('company.companyname')}>
                        {!detail.isEditing ? (
                            <Typography sx={sx14}>{item.companyName}</Typography>
                        ) : (
                            <TextField
                                size="small"
                                fullWidth
                                value={item.companyName}
                                onChange={e => detail.update('companyName', e.target.value)}
                                error={!hasCompanyName()}
                            />
                        )}
                    </FormRow>

                    {/* Company Type */}
                    <FormRow label={t('company.companytype')}>
                        {!detail.isNew ? (
                            <Typography sx={sx14}>{item.companyType?.companyTypeName}</Typography>
                        ) : (
                            <FormControl size="small" fullWidth>
                                <Select
                                    value={item.companyType?.id ?? ''}
                                    onChange={e => {
                                        const ct = companyTypes.find(c => c.id === e.target.value);
                                        if (ct) detail.update('companyType', ct);
                                    }}
                                >
                                    {[...companyTypes]
                                        .sort((a, b) => a.companyTypeName.localeCompare(b.companyTypeName))
                                        .map(c => (
                                            <MenuItem key={c.id} value={c.id}>{c.companyTypeName}</MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        )}
                    </FormRow>

                    {/* Reseller Price List */}
                    {isReseller && (
                        <FormRow label={t('resellerorder.resellerPriceList')}>
                            {!detail.isEditing ? (
                                <Typography sx={sx14}>{item.resellerPriceList?.pricelistName}</Typography>
                            ) : (
                                <FormControl size="small" fullWidth error={!hasResellerPriceList()}>
                                    <Select
                                        value={item.resellerPriceList?.id ?? ''}
                                        onChange={e => {
                                            const pl = priceLists.find(p => p.id === e.target.value);
                                            if (pl) detail.update('resellerPriceList', pl);
                                        }}
                                    >
                                        {[...priceLists]
                                            .sort((a, b) => a.pricelistName.localeCompare(b.pricelistName))
                                            .map(p => (
                                                <MenuItem key={p.id} value={p.id}>{p.pricelistName}</MenuItem>
                                            ))}
                                    </Select>
                                </FormControl>
                            )}
                        </FormRow>
                    )}

                    {/* Reseller Priority */}
                    {isReseller && (
                        <FormRow label={t('resellerorder.resellerPriority')}>
                            {!detail.isEditing ? (
                                <Typography sx={sx14}>{item.selectedPriority?.displayName}</Typography>
                            ) : (
                                <FormControl size="small" fullWidth>
                                    <Select
                                        value={item.selectedPriority?.code ?? ''}
                                        onChange={e => {
                                            const prio = resellerPriorities.find(p => p.code === e.target.value);
                                            if (prio) detail.update('selectedPriority', prio);
                                        }}
                                    >
                                        {resellerPriorities.map(p => (
                                            <MenuItem key={p.code} value={p.code}>{p.displayName}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        </FormRow>
                    )}

                    {/* Delivery Term */}
                    <FormRow label={t('company.deliveryTerm')}>
                        {!detail.isEditing ? (
                            <Typography sx={sx14}>{item.deliveryTerm?.name}</Typography>
                        ) : (
                            <FormControl size="small" fullWidth error={!hasDeliveryTerm()}>
                                <Select
                                    value={item.deliveryTerm?.id ?? ''}
                                    onChange={e => {
                                        const dt = deliveryTerms.find(d => d.id === e.target.value);
                                        if (dt) detail.update('deliveryTerm', dt);
                                    }}
                                >
                                    {[...deliveryTerms]
                                        .sort((a, b) => a.code.localeCompare(b.code))
                                        .map(d => (
                                            <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        )}
                    </FormRow>

                    {/* Payment Term */}
                    <FormRow label={t('company.paymentTerm')}>
                        {!detail.isEditing ? (
                            <Typography sx={sx14}>{item.paymentTerm?.name}</Typography>
                        ) : (
                            <FormControl size="small" fullWidth error={!hasPaymentTerm()}>
                                <Select
                                    value={item.paymentTerm?.id ?? ''}
                                    onChange={e => {
                                        const pt = paymentTerms.find(p => p.id === e.target.value);
                                        if (pt) detail.update('paymentTerm', pt);
                                    }}
                                >
                                    {[...paymentTerms]
                                        .sort((a, b) => a.code.localeCompare(b.code))
                                        .map(p => (
                                            <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        )}
                    </FormRow>

                    {/* Shipping Method */}
                    <FormRow label={t('company.shippingMethod')}>
                        {!detail.isEditing ? (
                            <Typography sx={sx14}>{item.shippingMethod?.name}</Typography>
                        ) : (
                            <FormControl size="small" fullWidth error={!hasShippingMethod()}>
                                <Select
                                    value={item.shippingMethod?.id ?? ''}
                                    onChange={e => {
                                        const sm = shippingMethods.find(s => s.id === e.target.value);
                                        if (sm) detail.update('shippingMethod', sm);
                                    }}
                                >
                                    {[...shippingMethods]
                                        .sort((a, b) => a.name.localeCompare(b.name))
                                        .map(s => (
                                            <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        )}
                    </FormRow>

                    <Divider />

                    {/* Organization Number */}
                    <FormRow label={t('company.organizationnumber')}>
                        {!detail.isEditing ? (
                            <Typography sx={sx14}>{item.organizationNumber}</Typography>
                        ) : (
                            <TextField
                                size="small"
                                fullWidth
                                value={item.organizationNumber ?? ''}
                                onChange={e => detail.update('organizationNumber', e.target.value)}
                            />
                        )}
                    </FormRow>

                    {/* Alternative Company Id */}
                    <FormRow label={t('company.alternativeCompanyId')}>
                        {!detail.isEditing ? (
                            <Typography sx={sx14}>{item.alternativeCompanyId}</Typography>
                        ) : (
                            <TextField
                                size="small"
                                fullWidth
                                value={item.alternativeCompanyId ?? ''}
                                onChange={e => detail.update('alternativeCompanyId', e.target.value)}
                            />
                        )}
                    </FormRow>

                    {/* Brand Connect Identifier */}
                    <FormRow label="Brand-Connect">
                        {!detail.isEditing ? (
                            <Typography sx={sx14}>{item.brandConnectIdentifier}</Typography>
                        ) : (
                            <TextField
                                size="small"
                                fullWidth
                                value={item.brandConnectIdentifier ?? ''}
                                onChange={e => detail.update('brandConnectIdentifier', e.target.value)}
                            />
                        )}
                    </FormRow>

                    <Divider />

                    {/* Primary Purchaser */}
                    <FormRow label={t('company.primaryPurchaser')}>
                        {!detail.isEditing ? (
                            <Typography sx={sx14}>
                                {item.primaryPurchaser && `${item.primaryPurchaser.firstName} ${item.primaryPurchaser.lastName}`}
                            </Typography>
                        ) : (
                            <FormControl size="small" fullWidth>
                                <Select
                                    value={item.primaryPurchaser?.id ?? ''}
                                    onChange={e => {
                                        const u = users.find(x => x.id === e.target.value);
                                        detail.update('primaryPurchaser', u ?? null);
                                    }}
                                >
                                    <MenuItem value=""><em>—</em></MenuItem>
                                    {[...users]
                                        .sort((a, b) => a.firstName.localeCompare(b.firstName))
                                        .map(u => (
                                            <MenuItem key={u.id} value={u.id}>{u.firstName} {u.lastName}</MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        )}
                    </FormRow>

                    {/* Purchasing Currency */}
                    <FormRow label={t('company.purchasingCurrency')}>
                        {!detail.isEditing ? (
                            <Typography sx={sx14}>
                                {item.purchasingCurrency && `${item.purchasingCurrency.code} ${item.purchasingCurrency.name}`}
                            </Typography>
                        ) : (
                            <FormControl size="small" fullWidth>
                                <Select
                                    value={item.purchasingCurrency?.id ?? ''}
                                    onChange={e => {
                                        const c = currencies.find(cu => cu.id === e.target.value);
                                        if (c) detail.update('purchasingCurrency', c);
                                    }}
                                >
                                    {[...currencies]
                                        .sort((a, b) => a.code.localeCompare(b.code))
                                        .map(c => (
                                            <MenuItem key={c.id} value={c.id}>{c.code}</MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        )}
                    </FormRow>

                    {/* Language */}
                    <FormRow label={t('company.language')}>
                        {!detail.isEditing ? (
                            <Typography sx={sx14}>
                                {item.language && `${item.language.locale} ${item.language.name}`}
                            </Typography>
                        ) : (
                            <FormControl size="small" fullWidth>
                                <Select
                                    value={item.language?.id ?? ''}
                                    onChange={e => {
                                        const l = languages.find(x => x.id === e.target.value);
                                        if (l) detail.update('language', l);
                                    }}
                                >
                                    {[...languages]
                                        .sort((a, b) => a.locale.localeCompare(b.locale))
                                        .map(l => (
                                            <MenuItem key={l.id} value={l.id}>{l.locale} {l.name}</MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        )}
                    </FormRow>

                    {/* Lead Time */}
                    <FormRow label={t('company.leadTime')}>
                        {!detail.isEditing ? (
                            <Typography sx={sx14}>{item.leadTime}</Typography>
                        ) : (
                            <TextField
                                size="small"
                                type="number"
                                fullWidth
                                value={item.leadTime ?? ''}
                                onChange={e => detail.update('leadTime', e.target.value === '' ? undefined : Number(e.target.value))}
                            />
                        )}
                    </FormRow>

                    {/* Price Model */}
                    <FormRow label={t('company.priceModel')}>
                        {!detail.isNew && !detail.isEditing ? (
                            <Typography sx={sx14}>{priceModelObject?.display}</Typography>
                        ) : (
                            <FormControl size="small" fullWidth>
                                <Select
                                    value={priceModelObject?.id ?? ''}
                                    onChange={e => {
                                        const pm = pricingModels.find(x => x.id === e.target.value);
                                        if (pm) {
                                            setPriceModelObject(pm);
                                            detail.update('priceModel', pm.name);
                                        }
                                    }}
                                >
                                    {[...pricingModels]
                                        .sort((a, b) => a.name.localeCompare(b.name))
                                        .map(pm => (
                                            <MenuItem key={pm.id} value={pm.id}>{pm.display}</MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        )}
                    </FormRow>

                    {/* Commission Percent */}
                    {priceModelObject?.name === 'commission' && (
                        <FormRow label={t('company.commissionPercent')}>
                            {!detail.isEditing ? (
                                <Typography sx={sx14}>{item.commissionPercent}</Typography>
                            ) : (
                                <TextField
                                    size="small"
                                    type="number"
                                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                                    fullWidth
                                    value={item.commissionPercent ?? 0}
                                    onChange={e => detail.update('commissionPercent', e.target.value === '' ? 0 : Number(e.target.value))}
                                />
                            )}
                        </FormRow>
                    )}

                    <Divider />

                    {/* Addresses */}
                    {(item.addresses ?? []).map(address => (
                        <FormRow key={address.addressType?.id} label={address.addressType?.typeName ?? ''}>
                            <AddressEditor
                                address={address}
                                isEditing={detail.isEditing}
                                onChange={(field, value) => updateAddressField(address, field, value)}
                                onRemove={() => removeAddress(address)}
                            />
                        </FormRow>
                    ))}

                    {detail.isEditing && availableAddressTypes.length > 0 && (
                        <FormRow label="">
                            <FormControl size="small" fullWidth>
                                <Select
                                    value=""
                                    displayEmpty
                                    onChange={e => {
                                        const at = addressTypes.find(a => a.id === e.target.value);
                                        if (at) addNewAddress(at);
                                    }}
                                >
                                    <MenuItem value=""><em>+ {t('user.newaddress')}</em></MenuItem>
                                    {availableAddressTypes.map(at => (
                                        <MenuItem key={at.id} value={at.id}>{at.typeName}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </FormRow>
                    )}

                    {/* Contact Infos */}
                    {(item.contactInfos ?? []).map(info => (
                        <FormRow key={info.contactInfoType?.id} label={info.contactInfoType?.typeName ?? ''}>
                            <ContactInfoEditor
                                contactInfo={info}
                                isEditing={detail.isEditing}
                                onChange={(field, value) => updateContactInfoField(info, field, value)}
                                onRemove={() => removeContactInfo(info)}
                            />
                        </FormRow>
                    ))}

                    {detail.isEditing && availableContactInfoTypes.length > 0 && (
                        <FormRow label="">
                            <FormControl size="small" fullWidth>
                                <Select
                                    value=""
                                    displayEmpty
                                    onChange={e => {
                                        const ct = contactInfoTypes.find(c => c.id === e.target.value);
                                        if (ct) addNewContactInfo(ct);
                                    }}
                                >
                                    <MenuItem value=""><em>+ {t('user.newcontactinfo')}</em></MenuItem>
                                    {availableContactInfoTypes.map(ct => (
                                        <MenuItem key={ct.id} value={ct.id}>{ct.typeName}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </FormRow>
                    )}
                </>);
            }}
        </DetailViewShell>
    );
}

interface AddressEditorProps {
    address: Address;
    isEditing: boolean;
    onChange: (field: string, value: string) => void;
    onRemove: () => void;
}

function AddressEditor({ address, isEditing, onChange, onRemove }: AddressEditorProps) {
    const { t } = useTranslation();
    const fields: { key: string; label: string }[] = [
        { key: 'addressLine1', label: t('address.addressline1') },
        { key: 'addressLine2', label: t('address.addressline2') },
        { key: 'addressLine3', label: t('address.addressline3') },
        { key: 'postalCode', label: t('address.postalCode') },
        { key: 'city', label: t('address.city') },
        { key: 'country', label: t('address.country') },
    ];

    if (!isEditing) {
        const parts = fields
            .map(f => address[f.key] as string | undefined)
            .filter(Boolean);
        return <Typography sx={sx14}>{parts.join(', ')}</Typography>;
    }

    return (
        <Stack spacing={1}>
            {fields.map(f => (
                <TextField
                    key={f.key}
                    size="small"
                    fullWidth
                    placeholder={f.label}
                    value={(address[f.key] as string | undefined) ?? ''}
                    onChange={e => onChange(f.key, e.target.value)}
                />
            ))}
            <Box>
                <Button size="small" color="error" variant="outlined" onClick={onRemove} sx={{ color: 'error.main' }}>
                    Remove
                </Button>
            </Box>
        </Stack>
    );
}

interface ContactInfoEditorProps {
    contactInfo: ContactInfo;
    isEditing: boolean;
    onChange: (field: string, value: string) => void;
    onRemove: () => void;
}

function ContactInfoEditor({ contactInfo, isEditing, onChange, onRemove }: ContactInfoEditorProps) {
    if (!isEditing) {
        return <Typography sx={sx14}>{contactInfo.value as string}</Typography>;
    }
    return (
        <Box display="flex" gap={1}>
            <TextField
                size="small"
                fullWidth
                value={(contactInfo.value as string | undefined) ?? ''}
                onChange={e => onChange('value', e.target.value)}
            />
            <Button size="small" color="error" variant="outlined" onClick={onRemove}>
                Remove
            </Button>
        </Box>
    );
}

function hydrateAddressTypes(company: Company, types: AddressType[]) {
    if (company.addresses) {
        company.addresses.forEach(a => {
            if (!a.addressType && a.addressTypeId) {
                const match = types.find(t => t.id === a.addressTypeId);
                if (match) a.addressType = match;
            }
        });
    }
}

function hydratePriority(company: Company, priorities: ResellerPriority[]) {
    if (company.priority) {
        company.selectedPriority = priorities.find(p => p.code.toLowerCase() === company.priority!.toLowerCase());
    } else {
        company.selectedPriority = priorities[4];
    }
}

function createCompanyRequest(company: Company, priceModelObject: PricingModel | null): CompanyRequest {
    const addresses = company.addresses?.map(a => ({
        ...a,
        addressTypeId: a.addressType?.id ?? a.addressTypeId,
    }));

    return {
        addresses,
        alternativeCompanyId: company.alternativeCompanyId,
        brands: company.brands,
        commissionPercent: (company.commissionPercent ?? 0) / 100,
        companyName: company.companyName,
        companySystemType: company.companyType?.companySystemType ?? 0,
        companyTypeId: company.companyType?.id ?? null,
        contactInfos: company.contactInfos,
        deliveryTermsId: company.deliveryTerm?.id ?? null,
        languageId: company.language?.id ?? null,
        leadTime: company.leadTime,
        organizationNumber: company.organizationNumber,
        paymentTermsId: company.paymentTerm?.id ?? null,
        priceModel: priceModelObject?.name,
        primaryPurchaserId: company.primaryPurchaser?.id ?? null,
        priority: company.selectedPriority?.code,
        purchasingCurrencyId: company.purchasingCurrency?.id ?? null,
        resellerPriceListId: company.resellerPriceList?.id ?? null,
        shippingMethodId: company.shippingMethod?.id ?? null,
        brandConnectIdentifier: company.brandConnectIdentifier,
    };
}
